import { memo, useEffect, useState } from "react"
import { Badge, Box, Button, Card, EmptyState, Flex, HStack, Stack, Text, VStack } from "@chakra-ui/react"
import WithLoginPage from "@/components/withlogin"
import CtvService from "services/ctv"
import { useApp } from "contexts/app"
import { formatCurrency } from "@/utilities/number"
import moment from "moment"
import DateRangePicker from "components/date-range-picker"

const service = new CtvService();

const typeFilters = [
    { key: "all", label: "Tất cả" },
    { key: "order", label: "Đơn hàng" },
    { key: "withdraw", label: "Rút tiền" },
]

const getStatusMeta = (status) => {
    if (status === "finish") {
        return { label: "Hoàn tất", colorPalette: "green" };
    }
    if (status === "pending") {
        return { label: "Đang chờ", colorPalette: "yellow" };
    }
    if (status === "canceled") {
        return { label: "Đã hủy", colorPalette: "red" };
    }
    return { label: status || "Không xác định", colorPalette: "gray" };
}

const CtvHistoryPage = () => {
    const { setPageTitle, setProcessing } = useApp();
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState({
        current_commission: 0,
        pending_commission: 0,
        total_earned: 0,
        total_withdrawn: 0,
    });
    const [pagination, setPagination] = useState({ page: 1, page_size: 20, total: 0, total_pages: 0 });
    const [filter, setFilter] = useState({
        type: "all",
        range: {
            start: moment().startOf("month"),
            end: moment().endOf("day"),
        },
    });

    useEffect(() => {
        setPageTitle("Lịch sử hoa hồng");
    }, [setPageTitle]);

    useEffect(() => {
        loadData(1, false);
    }, [filter]);

    const loadData = async (page = 1, append = false) => {
        setProcessing(true);
        try {
            const range = {
                start: filter.range.start.toDate(),
                end: filter.range.end.toDate(),
            };
            const data = await service.transactions({
                range,
                type: filter.type,
                status: "all",
                page,
                page_size: 20,
            });

            const list = Array.isArray(data?.items) ? data.items : [];
            setItems((prev) => (append ? [...prev, ...list] : list));
            setSummary(data?.summary || {
                current_commission: 0,
                pending_commission: 0,
                total_earned: 0,
                total_withdrawn: 0,
            });
            setPagination(data?.pagination || { page: 1, page_size: 20, total: 0, total_pages: 0 });
        } finally {
            setProcessing(false);
        }
    };

    const onFilterTypeChange = (type) => {
        setFilter((prev) => ({ ...prev, type }));
    };

    const onFilterRangeChange = (range) => {
        setFilter((prev) => ({ ...prev, range }));
    };

    const onLoadMore = () => {
        const nextPage = (pagination.page || 1) + 1;
        loadData(nextPage, true);
    };

    return (
        <div style={{ paddingBottom: 75 }}>
            <Stack gap={3}>
                <Card.Root size="sm">
                    <Card.Header>
                        <Text fontWeight="bold">Tổng quan hoa hồng</Text>
                    </Card.Header>
                    <Card.Body>
                        <Stack gap={2}>
                            <HStack justify="space-between">
                                <Text color="fg.muted">Số dư hiện tại</Text>
                                <Text fontWeight="bold">{formatCurrency(summary.current_commission || 0)}</Text>
                            </HStack>
                            <HStack justify="space-between">
                                <Text color="fg.muted">Đang chờ duyệt</Text>
                                <Text>{formatCurrency(summary.pending_commission || 0)}</Text>
                            </HStack>
                            <HStack justify="space-between">
                                <Text color="fg.muted">Hoa hồng phát sinh trong kỳ</Text>
                                <Text color="green.600">+{formatCurrency(summary.total_earned || 0)}</Text>
                            </HStack>
                            <HStack justify="space-between">
                                <Text color="fg.muted">Rút tiền phát sinh trong kỳ</Text>
                                <Text color="orange.600">-{formatCurrency(summary.total_withdrawn || 0)}</Text>
                            </HStack>
                        </Stack>
                    </Card.Body>
                </Card.Root>

                <Card.Root size="sm">
                    <Card.Header>
                        <HStack justify="space-between" width="100%">
                            <Text fontWeight="bold">Lịch sử giao dịch</Text>
                            <DateRangePicker value={filter.range} onChange={onFilterRangeChange}></DateRangePicker>
                        </HStack>
                    </Card.Header>
                    <Card.Body>
                        <Stack gap={3}>
                            <HStack>
                                {typeFilters.map((x) => (
                                    <Button
                                        key={x.key}
                                        size="sm"
                                        variant={x.key === filter.type ? "solid" : "outline"}
                                        onClick={() => onFilterTypeChange(x.key)}
                                    >
                                        {x.label}
                                    </Button>
                                ))}
                            </HStack>

                            {items.length === 0 && (
                                <EmptyState.Root>
                                    <EmptyState.Content>
                                        <EmptyState.Indicator />
                                        <VStack textAlign="center">
                                            <EmptyState.Title>Chưa có lịch sử hoa hồng</EmptyState.Title>
                                            <EmptyState.Description>
                                                Hãy chia sẻ link tiếp thị để bắt đầu nhận hoa hồng từ đơn hàng.
                                            </EmptyState.Description>
                                        </VStack>
                                    </EmptyState.Content>
                                </EmptyState.Root>
                            )}

                            {items.length > 0 && (
                                <Stack gap={2}>
                                    {items.map((item) => {
                                        const isOrder = item.type === "order";
                                        const amount = Math.abs(Number(item.amount || 0));
                                        const statusMeta = getStatusMeta(item.status);
                                        return (
                                            <Box key={item.id} borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3}>
                                                <HStack justify="space-between" align="start" mb={2}>
                                                    <Stack gap={1}>
                                                        <Text fontWeight="semibold">
                                                            {moment(item.create_on).format("DD/MM/YYYY HH:mm")}
                                                        </Text>
                                                        <HStack>
                                                            <Badge width="fit-content" colorPalette={isOrder ? "green" : "orange"}>
                                                                {isOrder ? "Hoa hồng đơn hàng" : "Rút tiền"}
                                                            </Badge>
                                                            <Badge width="fit-content" colorPalette={statusMeta.colorPalette}>
                                                                {statusMeta.label}
                                                            </Badge>
                                                        </HStack>
                                                    </Stack>
                                                    <Text fontWeight="bold" color={isOrder ? "green.600" : "orange.600"}>
                                                        {isOrder ? "+" : "-"}{formatCurrency(amount)}
                                                    </Text>
                                                </HStack>

                                                {isOrder ? (
                                                    <Stack gap={1}>
                                                        <Text>Đơn: {item.order_id || "-"}</Text>
                                                        {item.order?.customer_name && (
                                                            <Text color="fg.muted" fontSize="sm">Khách mua: {item.order.customer_name}</Text>
                                                        )}
                                                    </Stack>
                                                ) : (
                                                    <Text color="fg.muted" fontSize="sm">
                                                        {item.status === "pending"
                                                            ? "Yêu cầu rút tiền hoa hồng đang chờ xử lý."
                                                            : "Yêu cầu rút tiền hoa hồng đã xử lý."}
                                                    </Text>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            )}

                            {(pagination.page || 1) < (pagination.total_pages || 0) && (
                                <Flex justifyContent="center">
                                    <Button variant="outline" onClick={onLoadMore}>Xem thêm</Button>
                                </Flex>
                            )}
                        </Stack>
                    </Card.Body>
                </Card.Root>
            </Stack>
        </div>
    )
}

const withLogin = () => (
    <WithLoginPage>
        <CtvHistoryPage></CtvHistoryPage>
    </WithLoginPage>
)

export default memo(withLogin)
